import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { JobStatus } from "../../generated/core-api";

/**
 * `succeeded`/`failed` are terminal for every workloads job resource
 * (`ImportView`, `ValidationView`, `RunView`) — `JobStatus` and
 * `MigrationRunStatus` are two separately-named generated enums that share
 * the exact same `queued | running | succeeded | failed` values, so one
 * terminal-status list covers both.
 */
export const TERMINAL_JOB_STATUSES: string[] = [
  JobStatus.succeeded,
  JobStatus.failed,
];

export function isTerminalJobStatus(
  status: string | null | undefined
): boolean {
  return !!status && TERMINAL_JOB_STATUSES.includes(status);
}

/**
 * Generic poll-to-terminal for the LADR-039 workloads job endpoints —
 * `GET …/imports/{id}`, `GET …/validations/{id}`, `GET …/runs/{id}` all
 * follow the same "202 Accepted, then poll a `status` field to terminal"
 * shape as the Track 1 `useInvocationStatusPoll`. Shared here (rather than
 * re-implemented per resource) per the reuse note in
 * `.plans/ladr-039-contract-mapping.md`'s work-patterns row: Step 5
 * (`useOnboardingImport`) and the Step 5b/5c validation/run hooks all poll
 * through this one utility instead of each hand-rolling a `refetchInterval`
 * predicate.
 */
export function useOnboardingJobPoll<T extends { status: string }>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  enabled: boolean
): UseQueryResult<T> {
  return useQuery({
    queryKey,
    queryFn,
    enabled,
    refetchInterval: (query) => {
      // Stop when terminal OR when the poll itself failed — otherwise a
      // permanent GET error keeps refetching every 2s and managers stay
      // stuck in a non-terminal loading state (PR #450 review).
      if (query.state.error) {
        return false;
      }
      return isTerminalJobStatus(query.state.data?.status) ? false : 2000;
    },
    // Once terminal, the result never changes — don't let a window-focus
    // regain re-fetch a settled job (same rationale as
    // `useInvocationStatusPoll`).
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
